import { useSponsorships, useDeleteSponsorship } from "@/api/sponsorships";
import { useState, useMemo } from "react";
import {
  Plus,
  Banknote,
  Download,
  Award,
  ChevronDown,
  ChevronUp,
  Receipt,
  Trash2,
  FileSpreadsheet,
  FileText,
  Pencil,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { currencies } from "@/lib/constants";
import { RecordPaymentModal } from "@/components/sponsorship/RecordPaymentModal";
import { CertificateModal } from "@/components/sponsorship/CertificateModal";
import { PaymentReceiptModal } from "@/components/payment/PaymentReceiptModal";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchInput } from "@/components/ui/search-input";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { AddSponsorshipForm } from "./components/add-sponsorship-form";
import { EditSponsorshipModal } from "@/components/sponsorship/EditSponsorshipModal";
import { useSearchParams } from "react-router-dom";
import { exportToExcel, exportToPDF } from "@/lib/export";

/* ── Expandable Table Row ── */
function SponsorshipRow({
  sponsorship,
  idx,
  onRecordPayment,
  onCertificate,
  onReceipt,
  onDelete,
  onEdit,
}) {
  const sym = currencies[sponsorship.currency || "INR"]?.symbol || "₹";
  const remaining = sponsorship.amountPromised - sponsorship.paidAmount;
  const pct =
    sponsorship.amountPromised > 0
      ? Math.min(sponsorship.paidAmount / sponsorship.amountPromised, 1)
      : 0;
  const [expanded, setExpanded] = useState(false);

  const statusClass =
    sponsorship.status === "completed"
      ? "bg-emerald-100 text-emerald-700"
      : sponsorship.status === "active"
        ? "bg-blue-100 text-blue-700"
        : "bg-amber-100 text-amber-700";
  const statusLabel =
    sponsorship.status === "completed"
      ? "Paid"
      : sponsorship.status === "active"
        ? "Partial"
        : "Pending";

  const payments = sponsorship.payments || [];
  const chapterName = sponsorship.chapter?.chapterName || "";

  return (
    <>
      <tr
        className={cn(
          "border-b border-border/30 hover:bg-blue-50/50 transition-colors cursor-pointer",
          idx % 2 === 0 ? "bg-background" : "bg-muted/20",
        )}
        onClick={() => payments.length > 0 && setExpanded(!expanded)}
      >
        {/* Expand */}
        <td className="px-2 py-1.5 border-r border-border/30 text-center">
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5"
            disabled={payments.length === 0}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? (
              <ChevronUp className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
          </Button>
        </td>
        {/* # */}
        <td className="px-2 py-1.5 border-r border-border/30 text-muted-foreground tabular-nums text-xs">
          {idx + 1}
        </td>
        {/* ID */}
        <td className="px-2 py-1.5 border-r border-border/30 font-mono text-muted-foreground text-xs">
          {sponsorship.sponsorshipId}
        </td>
        {/* Sponsor */}
        <td className="px-2 py-1.5 border-r border-border/30 text-xs">
          <div className="font-medium truncate max-w-[150px]">
            {sponsorship.sponsorName}
          </div>
          {sponsorship.careOf?.name && (
            <div className="text-[10px] text-muted-foreground truncate">
              c/o {sponsorship.careOf.name}
            </div>
          )}
        </td>
        {/* Phone */}
        <td className="px-2 py-1.5 border-r border-border/30 text-xs text-muted-foreground">
          {sponsorship.sponsorPhone || "—"}
        </td>
        {/* Mahallu */}
        <td className="px-2 py-1.5 border-r border-border/30 text-xs truncate max-w-[120px]">
          {sponsorship.mahalluName || "—"}
        </td>
        {/* Chapter */}
        <td className="px-2 py-1.5 border-r border-border/30 text-xs truncate max-w-[100px]">
          {chapterName || "—"}
        </td>
        {/* Pledged */}
        <td className="px-2 py-1.5 border-r border-border/30 text-xs text-right font-semibold tabular-nums">
          {sym}
          {sponsorship.amountPromised?.toLocaleString()}
        </td>
        {/* Collected */}
        <td className="px-2 py-1.5 border-r border-border/30 text-xs text-right font-bold tabular-nums text-emerald-600">
          {sym}
          {sponsorship.paidAmount?.toLocaleString()}
        </td>
        {/* Balance */}
        <td
          className={cn(
            "px-2 py-1.5 border-r border-border/30 text-xs text-right font-semibold tabular-nums",
            remaining > 0 ? "text-amber-600" : "text-muted-foreground",
          )}
        >
          {sym}
          {remaining.toLocaleString()}
        </td>
        {/* Progress */}
        <td className="px-2 py-1.5 border-r border-border/30">
          <div className="flex items-center gap-1">
            <div className="w-12 h-1.5 bg-secondary rounded-full overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-500",
                  pct >= 1
                    ? "bg-emerald-500"
                    : pct > 0
                      ? "bg-primary"
                      : "bg-muted-foreground/20",
                )}
                style={{ width: `${pct * 100}%` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground tabular-nums w-7">
              {Math.round(pct * 100)}%
            </span>
          </div>
        </td>
        {/* Status */}
        <td className="px-2 py-1.5 border-r border-border/30 text-center">
          <span
            className={cn(
              "text-[9px] font-semibold px-1.5 py-0.5 rounded-full",
              statusClass,
            )}
          >
            {statusLabel}
          </span>
        </td>
        {/* Date */}
        <td className="px-2 py-1.5 border-r border-border/30 text-center text-xs text-muted-foreground">
          {new Date(sponsorship.createdAt).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </td>
        {/* Actions */}
        <td className="px-2 py-1.5 text-center">
          <div className="flex items-center justify-center gap-0.5">
            {remaining > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-emerald-600 hover:bg-emerald-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRecordPayment(sponsorship);
                    }}
                  >
                    <Banknote className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Record Payment</TooltipContent>
              </Tooltip>
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-violet-600 hover:bg-violet-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCertificate(sponsorship);
                  }}
                >
                  <Award className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Certificate</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-blue-600 hover:bg-blue-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(sponsorship);
                  }}
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Edit</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 text-destructive hover:bg-destructive/10"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(sponsorship._id);
                  }}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Delete</TooltipContent>
            </Tooltip>
          </div>
        </td>
      </tr>

      {/* Expanded Payment History */}
      {expanded && payments.length > 0 && (
        <tr>
          <td colSpan={13} className="p-0">
            <div className="bg-muted/30 border-b">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[30%]">
                      Payment ID
                    </th>
                    <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[40%]">
                      Date
                    </th>
                    <th className="text-right font-semibold text-muted-foreground uppercase tracking-wider px-4 py-1.5 w-[20%]">
                      Amount
                    </th>
                    <th className="text-center px-4 py-1.5 w-[10%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {payments
                    .slice()
                    .sort(
                      (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
                    )
                    .map((p) => (
                      <tr
                        key={p._id}
                        className="border-b border-border/30 last:border-b-0 hover:bg-muted/50"
                      >
                        <td className="px-4 py-1.5 font-mono text-muted-foreground">
                          #{p.paymentId}
                        </td>
                        <td className="px-4 py-1.5 text-muted-foreground">
                          {new Date(p.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                          {" · "}
                          {new Date(p.createdAt).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })}
                        </td>
                        <td className="px-4 py-1.5 text-right font-semibold text-emerald-600 tabular-nums">
                          +{sym}
                          {p.amount?.toLocaleString()}
                        </td>
                        <td className="px-4 py-1.5 text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-5 w-5 text-muted-foreground hover:text-foreground"
                                onClick={() =>
                                  onReceipt({
                                    paymentId: p.paymentId,
                                    name: sponsorship.sponsorName,
                                    place:
                                      sponsorship.mahalluName ||
                                      sponsorship.place ||
                                      "",
                                    amount: p.amount,
                                    currency: sponsorship.currency || "INR",
                                    createdAt: p.createdAt,
                                  })
                                }
                              >
                                <Download className="h-3 w-3" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom">
                              Download Receipt
                            </TooltipContent>
                          </Tooltip>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function AdminCommunity() {
  const { data: sponsorships, isLoading } = useSponsorships();
  const { mutate: deleteSponsorship } = useDeleteSponsorship();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialChapter = searchParams.get("chapter") || "All";
  const [activeChapter, setActiveChapter] = useState(initialChapter);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [payTarget, setPayTarget] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [certTarget, setCertTarget] = useState(null);
  const [receiptTarget, setReceiptTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);

  // Only Mahallu type
  const mahalluSponsorships = useMemo(
    () => (sponsorships || []).filter((sp) => sp.type === "Mahallu"),
    [sponsorships],
  );

  // Chapter filter options
  const chapterOptions = useMemo(() => {
    const names = [
      ...new Set(
        mahalluSponsorships
          .map((sp) => {
            const ch = sp.chapter;
            return ch
              ? { id: ch._id || ch, name: ch.chapterName || "Unknown" }
              : null;
          })
          .filter(Boolean)
          .map((ch) => JSON.stringify(ch)),
      ),
    ].map((s) => JSON.parse(s));
    return [
      { id: "All", name: "All Chapters" },
      ...names.sort((a, b) => a.name.localeCompare(b.name)),
    ];
  }, [mahalluSponsorships]);

  // Filtered list
  const filtered = useMemo(() => {
    let list = mahalluSponsorships;
    if (activeChapter !== "All") {
      list = list.filter((sp) => {
        const chId = sp.chapter?._id || sp.chapter;
        return chId === activeChapter;
      });
    }
    if (statusFilter !== "all") {
      list = list.filter((sp) => sp.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (sp) =>
          sp.sponsorName?.toLowerCase().includes(q) ||
          sp.mahalluName?.toLowerCase().includes(q) ||
          sp.careOf?.name?.toLowerCase().includes(q) ||
          sp.sponsorshipId?.toLowerCase().includes(q) ||
          sp.sponsorPhone?.includes(q),
      );
    }
    return list;
  }, [mahalluSponsorships, activeChapter, statusFilter, search]);

  /* ── Export helpers ── */
  const getExportColumns = () => [
    { header: "Sponsorship ID", key: "sponsorshipId" },
    { header: "Sponsor", key: "sponsorName" },
    { header: "Phone", key: "sponsorPhone" },
    { header: "Mahallu", key: "mahalluName" },
    { header: "Care Of", key: "careOf" },
    { header: "Chapter", key: "chapterName" },
    { header: "Pledged", key: "pledged" },
    { header: "Collected", key: "collected" },
    { header: "Balance", key: "balance" },
    { header: "Status", key: "status" },
  ];

  const prepareExportRows = (data) =>
    data.map((sp) => ({
      sponsorshipId: sp.sponsorshipId,
      sponsorName: sp.sponsorName,
      sponsorPhone: sp.sponsorPhone || "",
      mahalluName: sp.mahalluName || "",
      careOf: sp.careOf?.name || "",
      chapterName: sp.chapter?.chapterName || "",
      pledged: sp.amountPromised,
      collected: sp.paidAmount,
      balance: sp.amountPromised - sp.paidAmount,
      status: sp.status,
    }));

  const handleExportExcel = (useFiltered) => {
    const data = useFiltered ? filtered : mahalluSponsorships;
    exportToExcel({
      columns: getExportColumns(),
      rows: prepareExportRows(data),
      fileName: `community-sponsorships-${useFiltered ? "filtered" : "all"}-${new Date().toISOString().slice(0, 10)}`,
    });
  };

  const handleExportPDF = (useFiltered) => {
    const data = useFiltered ? filtered : mahalluSponsorships;
    exportToPDF({
      columns: getExportColumns(),
      rows: prepareExportRows(data),
      fileName: `community-sponsorships-${useFiltered ? "filtered" : "all"}-${new Date().toISOString().slice(0, 10)}`,
      title: `Mahallu Sponsorships${useFiltered ? " (Filtered)" : ""}`,
    });
  };

  // Summary stats
  const summary = useMemo(() => {
    const pledged = filtered.reduce((s, sp) => s + (sp.amountPromised || 0), 0);
    const collected = filtered.reduce((s, sp) => s + (sp.paidAmount || 0), 0);
    return {
      pledged,
      collected,
      remaining: pledged - collected,
      count: filtered.length,
    };
  }, [filtered]);

  const handleChapterFilter = (chId) => {
    setActiveChapter(chId);
    if (chId === "All") {
      searchParams.delete("chapter");
    } else {
      searchParams.set("chapter", chId);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const handleDelete = (id) => {
    if (window.confirm("Delete this sponsorship? This cannot be undone.")) {
      deleteSponsorship(id);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <Skeleton className="h-10 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-full" />
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full">
      {/* ── Header ── */}
      <div className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b -mx-3 md:-mx-6 px-3 md:px-6">
        <div className="pt-3 pb-1 flex items-start justify-between">
          <div>
            <h1 className="text-lg font-semibold leading-tight">Community</h1>
            <p className="text-xs text-muted-foreground">
              Global Mahallu Pledges · {summary.count} sponsorships
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Export menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-8 w-8">
                  <Download className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={() => handleExportExcel(true)}>
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                  Excel (Filtered)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportExcel(false)}>
                  <FileSpreadsheet className="h-3.5 w-3.5 mr-2 text-emerald-600" />
                  Excel (All Data)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportPDF(true)}>
                  <FileText className="h-3.5 w-3.5 mr-2 text-red-600" />
                  PDF (Filtered)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportPDF(false)}>
                  <FileText className="h-3.5 w-3.5 mr-2 text-red-600" />
                  PDF (All Data)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              size="sm"
              onClick={() => setShowAdd(true)}
              className="gap-1.5"
            >
              <Plus className="h-4 w-4" />
              Add
            </Button>
          </div>
        </div>

        {/* Chapter filter pills */}
        <ScrollArea className="w-full">
          <div className="flex gap-2 py-2">
            {chapterOptions.map((ch) => (
              <Button
                key={ch.id}
                variant={activeChapter === ch.id ? "default" : "outline"}
                size="sm"
                className="rounded-full shrink-0 h-7 text-xs px-3"
                onClick={() => handleChapterFilter(ch.id)}
              >
                {ch.name}
              </Button>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        {/* Search + Status filter */}
        <div className="flex items-center gap-2 pb-2">
          <div className="flex-1">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, mahallu, phone, or ID..."
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[110px] h-8 text-xs shrink-0">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="active">Partial</SelectItem>
              <SelectItem value="completed">Paid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ── Summary Bar ── */}
      <div className="px-4 py-2 flex items-center gap-4 sm:gap-6 border-b bg-muted/30 text-xs">
        <div>
          <span className="text-muted-foreground">Total Pledged:</span>{" "}
          <span className="font-bold tabular-nums">
            ₹{summary.pledged.toLocaleString()}
          </span>
        </div>
        <div>
          <span className="text-muted-foreground">Collected:</span>{" "}
          <span className="font-bold tabular-nums text-emerald-600">
            ₹{summary.collected.toLocaleString()}
          </span>
        </div>
        <div className="hidden sm:block">
          <span className="text-muted-foreground">Balance:</span>{" "}
          <span className="font-bold tabular-nums text-amber-600">
            ₹{summary.remaining.toLocaleString()}
          </span>
        </div>
      </div>

      {/* ── Excel-style Table ── */}
      <div className="flex-1 overflow-x-auto">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Receipt className="h-10 w-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">
              No mahallu pledges found
            </p>
          </div>
        ) : (
          <table className="w-full text-xs border-collapse min-w-[1050px]">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-2 py-2 border-r border-border/40 w-[3%]"></th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[4%]">
                  #
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  ID
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[13%]">
                  Sponsor
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  Phone
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[10%]">
                  Mahallu
                </th>
                <th className="text-left font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[9%]">
                  Chapter
                </th>
                <th className="text-right font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  Pledged
                </th>
                <th className="text-right font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  Collected
                </th>
                <th className="text-right font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  Balance
                </th>
                <th className="text-center font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  Progress
                </th>
                <th className="text-center font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[5%]">
                  Status
                </th>
                <th className="text-center font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 border-r border-border/40 w-[8%]">
                  Date
                </th>
                <th className="text-center font-semibold text-muted-foreground uppercase tracking-wider px-2 py-2 w-[8%]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <TooltipProvider delayDuration={300}>
                {filtered.map((sp, idx) => (
                  <SponsorshipRow
                    key={sp._id}
                    sponsorship={sp}
                    idx={idx}
                    onRecordPayment={setPayTarget}
                    onCertificate={setCertTarget}
                    onReceipt={setReceiptTarget}
                    onDelete={handleDelete}
                    onEdit={setEditTarget}
                  />
                ))}
              </TooltipProvider>
            </tbody>
          </table>
        )}
      </div>

      {/* Modals */}
      {payTarget && (
        <RecordPaymentModal
          isOpen={!!payTarget}
          onClose={() => setPayTarget(null)}
          sponsorship={payTarget}
        />
      )}

      {certTarget && (
        <CertificateModal
          isOpen={!!certTarget}
          onClose={() => setCertTarget(null)}
          sponsorship={certTarget}
        />
      )}

      {receiptTarget && (
        <PaymentReceiptModal
          isOpen={!!receiptTarget}
          onClose={() => setReceiptTarget(null)}
          payment={receiptTarget}
        />
      )}
      <EditSponsorshipModal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        sponsorship={editTarget}
      />
      {/* Add Sponsorship Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-2xl p-4 pt-6">
          <DialogHeader className="pb-4">
            <DialogTitle>New Mahallu Sponsorship</DialogTitle>
            <DialogDescription className="text-xs">
              Create a Mahallu sponsorship for any chapter
            </DialogDescription>
          </DialogHeader>
          <AddSponsorshipForm
            onSuccess={() => setShowAdd(false)}
            defaultType="Mahallu"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
